import { useEffect, useState } from 'react'
import { api, type BriefingRow, type BriefingDetail } from '../api.js'
import { ErrorBox } from './Dashboard.js'
import { FeedbackBar } from '../components/FeedbackBar.js'

export function Brief() {
  const [rows, setRows] = useState<BriefingRow[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [activeDate, setActiveDate] = useState<string | null>(null)
  const [regenerating, setRegenerating] = useState(false)
  const [regenErr, setRegenErr] = useState<string | null>(null)

  function loadList() {
    return api
      .briefings(30)
      .then((list) => {
        setRows(list)
        if (list.length > 0 && !activeDate) setActiveDate(list[0]!.date)
      })
      .catch((e) => setErr(e.message))
  }

  useEffect(() => {
    loadList()
  }, [])

  async function handleRegenerate() {
    setRegenerating(true)
    setRegenErr(null)
    try {
      const result = await api.briefRegenerate()
      await loadList()
      setActiveDate(result.date)
    } catch (e) {
      setRegenErr(e instanceof Error ? e.message : String(e))
    } finally {
      setRegenerating(false)
    }
  }

  if (err) return <ErrorBox message={err} />
  if (!rows) return <div className="text-neutral-400">Loading...</div>

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Daily Brief</h1>
          <p className="text-sm text-neutral-400 mt-1">
            Briefings generated by the nightly-brief agent and stored in Hub.
          </p>
        </div>
        <button
          type="button"
          onClick={handleRegenerate}
          disabled={regenerating}
          className="shrink-0 rounded-md border border-neutral-700 bg-neutral-800 px-3 py-1.5 text-sm text-neutral-200 hover:bg-neutral-700 disabled:opacity-50 transition-colors"
        >
          {regenerating ? 'Generating…' : 'Regenerate'}
        </button>
      </div>

      {regenErr && (
        <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-400">
          {regenErr}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-6 text-sm text-neutral-400">
          <p className="mb-2">No briefings generated yet.</p>
          <p className="text-neutral-500">
            Run <code className="font-mono text-neutral-300">hub brief</code> from the CLI, or click
            Regenerate above to generate today's brief. Set{' '}
            <code className="font-mono text-neutral-300">HUB_BRIEF_ENABLED=1</code> on the server to
            enable the 05:00 + 22:00 cron jobs.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-4">
          <aside className="rounded-lg border border-neutral-800 overflow-hidden self-start">
            <ul className="divide-y divide-neutral-800 text-sm max-h-[70vh] overflow-y-auto">
              {rows.map((r) => (
                <li key={r.date}>
                  <button
                    type="button"
                    onClick={() => setActiveDate(r.date)}
                    className={[
                      'w-full text-left px-3 py-2 transition-colors',
                      activeDate === r.date
                        ? 'bg-neutral-800 text-neutral-100'
                        : 'hover:bg-neutral-900/50 text-neutral-300',
                    ].join(' ')}
                  >
                    <div className="font-mono">{r.date}</div>
                    <div className="text-xs text-neutral-500">
                      {new Date(r.generatedAt).toLocaleString()}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>
          <section className="rounded-lg border border-neutral-800 bg-neutral-900/30 p-4 min-h-[60vh]">
            {activeDate ? <BriefBody date={activeDate} /> : null}
          </section>
        </div>
      )}
    </div>
  )
}

function BriefBody({ date }: { date: string }) {
  const [detail, setDetail] = useState<BriefingDetail | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    setDetail(null)
    setErr(null)
    api
      .briefingDetail(date)
      .then(setDetail)
      .catch((e) => setErr(e.message))
  }, [date])

  if (err) return <p className="text-red-400 text-sm">{err}</p>
  if (!detail) return <p className="text-neutral-500 text-sm">Loading...</p>

  return (
    <article className="space-y-3">
      <header className="flex flex-wrap items-baseline gap-3 pb-2 border-b border-neutral-800">
        <h2 className="text-lg font-semibold font-mono">{detail.date}</h2>
        <span className="text-xs text-neutral-500">run {detail.runId.slice(0, 10)}</span>
        <span className="ml-auto text-xs text-neutral-600">
          {new Date(detail.generatedAt).toLocaleString()}
        </span>
      </header>
      {detail.body ? (
        <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-neutral-200">
          {detail.body}
        </pre>
      ) : (
        <p className="text-neutral-500 text-sm">
          Body not available — regenerate to re-run this brief.
        </p>
      )}
      <FeedbackBar sourceType="brief" sourceId={detail.runId} />
    </article>
  )
}
